/**
 * locationHistoryService – mirrors LocationHistoryService (location_history_service.dart).
 *
 * Stores up to MAX_HISTORY recent location selections in localStorage,
 * using the same deduplication logic as the mobile app.
 *
 * Mobile uses SharedPreferences; web uses localStorage (same semantics).
 */

import type { Place } from './locationService';

const LS_KEY = 'musicade_location_history';
const MAX_HISTORY = 10; // matches mobile _maxHistoryItems

function _readHistory(): Place[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as Place[];
  } catch {
    return [];
  }
}

function _writeHistory(items: Place[]): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  } catch {
    // Silently ignore storage errors (quota exceeded, etc.)
  }
}

/** Normalise a string for comparison (trim + collapse whitespace). */
function _norm(s?: string): string {
  return (s ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export const locationHistoryService = {
  /** Retrieve all saved locations (most recent first). */
  getHistory(): Place[] {
    return _readHistory().filter((p) => p.city?.trim() && p.state?.trim() && p.country?.trim());
  },

  /**
   * Save a place to history.
   * Mirrors mobile saveLocationToHistory() – deduplicates before inserting.
   */
  saveToHistory(place: Place): void {
    const history = _readHistory();

    // Deduplicate – same criteria as mobile
    const deduped = history.filter((h) => {
      const samePlaceId = h.placeId && h.placeId === place.placeId;
      const sameCoords = h.lat === place.lat && h.lon === place.lon;
      const sameName = _norm(h.displayName) === _norm(place.displayName);
      const sameCity =
        h.city &&
        place.city &&
        _norm(h.city) === _norm(place.city) &&
        _norm(h.state) === _norm(place.state) &&
        _norm(h.country) === _norm(place.country);

      return !(samePlaceId || sameCoords || sameName || sameCity);
    });

    deduped.unshift(place);
    _writeHistory(deduped.slice(0, MAX_HISTORY));
  },

  /** Remove a single entry by placeId. */
  removeFromHistory(placeId: string): void {
    _writeHistory(_readHistory().filter((h) => h.placeId !== placeId));
  },

  /** Wipe the entire location history. */
  clearHistory(): void {
    localStorage.removeItem(LS_KEY);
  },
};
